import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Alert,
  Button,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import { getCookie, setCookie } from '@utils/getCookie';
import parse from 'html-react-parser';
import { FC } from 'react';

type UserDetailRowProps = {
  label: string;
  value?: string | null;
  tooltip?: string;
  isHtml?: boolean;
  action?: () => void;
  isEdit?: boolean;
  noLabel?: boolean;
  icon?: any;
  isCalenderLabel?: any;
  companyName?: any;
  isAlert?: any;
  setShowCalnderAlert?: any;
  textAlign?: boolean;
};

const UserDetailRow: FC<UserDetailRowProps> = ({
  label,
  value,
  tooltip,
  isHtml,
  action,
  isEdit,
  noLabel,
  icon,
  isCalenderLabel,
  companyName,
  isAlert,
  setShowCalnderAlert,
  textAlign,
}) => {
  return (
    <>
      <Grid
        container
        alignItems="center"
        bgcolor={white}
        borderRadius={8}
        py={2}
      >
        {isAlert && (
          <Alert
            sx={{
              fontSize: '12px',
              padding: '1px 7px',
              '& .MuiAlert-action': {
                paddingTop: '0px',
                paddingBottom: '0px',
              },
            }}
            severity="info"
            onClose={() => {
              setShowCalnderAlert(false);
              setCookie('calendarSyncRead', 'true', 5);
            }}
          >
            Google Calendar Connected. Upcoming interviews will be added to your
            connected calendar.
          </Alert>
        )}
        {!noLabel && (
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={2} alignItems="center">
              {label && (
                <Typography
                  id={`label-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  fontWeight={medium}
                >
                  {label}:
                </Typography>
              )}

              {tooltip && (
                <Tooltip title={tooltip} placement="top" arrow>
                  <HelpOutlineIcon
                    sx={{
                      fontSize: '18px',
                      color: 'grey.400',
                      transition: 'color 0.35s ease',
                      '&:hover': { color: 'grey.900' },
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Grid>
        )}

        <>
          {!isAlert && (
            <Grid
              item
              xs={12}
              sm={noLabel ? 8 : 4}
              justifyContent="center"
              display="flex"
              flexDirection="column"
              textAlign="center"
            >
              {companyName && (
                <Typography
                  color={value || isEdit ? 'text.primary' : 'text.secondary'}
                  fontStyle={value ? 'normal' : 'italic'}
                  whiteSpace="pretty"
                  sx={{ wordBreak: 'break-all', mb: '2px' }}
                  id={`value-${label?.toLowerCase().replace(/\s+/g, '-')}`}
                  fontWeight="600"
                >
                  {companyName}
                </Typography>
              )}

              <Typography
                color={value || isEdit ? 'text.primary' : 'text.secondary'}
                fontStyle={value ? 'normal' : 'italic'}
                whiteSpace="pretty"
                sx={{
                  wordBreak: 'break-all',
                  textAlign: textAlign ? 'left' : '',
                }}
                id={`value-${label?.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isHtml
                  ? parse(value || '')
                  : isCalenderLabel
                    ? ''
                    : value || 'None'}
              </Typography>
            </Grid>
          )}

          {!isAlert && (
            <Grid item xs={12} sm={4} justifyContent="end" display="flex">
              <Button
                id={`edit-button-${label.toLowerCase().replace(/\s+/g, '-')}`}
                color={
                  isCalenderLabel || isEdit || value ? 'primary' : 'success'
                }
                variant="contained"
                startIcon={
                  icon ||
                  (value || isEdit ? (
                    <EditOutlinedIcon sx={{ fontSize: '16px' }} />
                  ) : (
                    <AddOutlinedIcon sx={{ fontSize: '18px' }} />
                  ))
                }
                onClick={action}
                sx={{
                  padding: '4px 15px',
                  fontSize: '14px',
                }}
              >
                {isCalenderLabel
                  ? 'Integrate with Google Calendar'
                  : value || isEdit
                    ? 'Edit'
                    : 'Add'}
              </Button>
            </Grid>
          )}
        </>
      </Grid>
      {!noLabel && <Divider sx={{ marginTop: '7px' }} />}
    </>
  );
};

export default UserDetailRow;
